/**
 * The COP stage outside a manual session: does the operator want the picture
 * beside the map?
 *
 * Manual flight has three layouts (`manual-layout.ts`); regular mode has two,
 * because VIDEO has no meaning without a control minimap. So this is a single
 * yes/no, and it is deliberately NOT the manual layout key: choosing SPLIT here
 * must never change what the operator gets on their next MANUAL, and the other
 * way round (docs/PLANS/cop-side-by-side-video/IMPLEMENTATION.md, D3 and D5).
 *
 * Plain values and pure functions, like its manual sibling: no React, no DOM,
 * so the rules can be tested without rendering a COP.
 */

const STORAGE_PREFIX = 'cop_split_video';

/** Stored as this literal; anything else a build wrote is read as off. */
const STORED_ON = '1';

/**
 * Per operator, for the reason `manualLayoutStorageKey` gives: a shared console
 * is a real deployment, and two operators signing into the same browser must not
 * inherit each other's stage. With no identity, one shared anonymous slot.
 */
export function splitVideoStorageKey(operator?: string | null): string {
  const who = operator?.trim();
  return who ? `${STORAGE_PREFIX}:${who}` : STORAGE_PREFIX;
}

/**
 * The operator's stored choice. Off for anyone who has never chosen.
 *
 * Never throws: `localStorage` can throw outright (privacy mode, locked-down
 * enterprise policy). A preference that cannot be read is a preference, not a
 * fault, and must never take the COP down with it.
 */
export function readSplitVideoPreference(operator?: string | null): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(splitVideoStorageKey(operator)) === STORED_ON;
  } catch {
    return false;
  }
}

/** Returns false when the choice could not be persisted, so a caller can say so. */
export function writeSplitVideoPreference(on: boolean, operator?: string | null): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const key = splitVideoStorageKey(operator);
    if (on) window.localStorage.setItem(key, STORED_ON);
    else window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}
